// Skill effect resolution: turn a Skill (with its Effect[]) into observable
// state changes on the targets and event-log entries.
//
// Targeting:
//   self / singleTarget / circle / line / cone / allyLowestHp / allAllies
//
// Effects implemented:
//   PR A:   damage / heal / shield / applyDebuff / applyBuff / cleanse
//   PR B-1: placePlaceable / summon
//             + damage.elementBranch dispatch (post-resolve)
//             + applyBuff.onCritEffects retained on the BuffStack
//             + Effect.delayTicks scheduling (queue at apply time)
//             + fireOnCritHooks helper (called from damage path)
//   PR B-2: damageConversion / anchoredHealOnHit / reflectingProjectile /
//           transformOnHit / phaseCyclingAura / counterStance /
//           sharedAbsorbCharge / damageStoreAndRelease (still no-op stubs here)
//   P55-024: dash / knockback / pull / consumeNextCast (movement effects).
//           dash closes the caster onto the target; knockback/pull displace the
//           target outward/inward; consumeNextCast tags a one-shot cast boost.
//
// Damage element resolution: ADR 0008 §2.10 / §2.11. `Effect.damage.element`
// may carry the literal `'weapon-derived'`; this module always passes a
// concrete `Element` to the damage pipeline and the BattleEvent log.
import { PERMANENT_BUFF_EXPIRES_AT } from '@renavora/shared';
import { activateDamageReactEvent } from './ai/damage-react.js';
import { onKillEvent, onTakenDamageReset, resolveKillOwner } from './battle/kill-attribution.js';
import { applyMarkStack } from './battle/marks.js';
import { projectileShieldMissChanceBonus, spawnPlaceable, } from './battle/placeables.js';
import { fireReactiveSkills } from './battle/reactive-events.js';
import { enqueueEffect } from './battle/scheduling.js';
import { spawnSummonsAttachingAura } from './battle/summons.js';
import { applyDamageWithHooks, resolveReflectingProjectile, resolveTransformOnHit, } from './battle/ult-mechanics.js';
import { calculateDamage } from './damage.js';
import { resolveDamageElement } from './element-resolver.js';
/** Maximum stack count for stacking-style buffs / debuffs. Matches §5.12. */
const STACK_CAP = 3;
/**
 * Lookup current stack count of a status on an entity. Walks both buffs and
 * debuffs lists. Returns 0 if absent. ADR 0014 §2.1 (scalesWithStatusStacks /
 * chanceScalesWithStatusStacks 解決用).
 */
export function stackCountOf(entity, statusId) {
    const inBuffs = entity.buffs.find((b) => b.id === statusId);
    if (inBuffs !== undefined)
        return inBuffs.stacks;
    const inDebuffs = entity.debuffs.find((b) => b.id === statusId);
    if (inDebuffs !== undefined)
        return inDebuffs.stacks;
    return 0;
}
/**
 * Fire reactive ActivationEvents triggered by a damage application. ADR 0014
 * §2.1 (機構 2) / §2.4 + 軸 9 §6.1 (召喚体 kill 帰属 + kill_streak)。
 *
 * Event mapping (called once per damage application):
 *   - `onHit`            → attacker side, when result.hit && damage > 0
 *   - `onCrit`           → attacker side, when result.crit
 *   - `onTakenDamage`    → target side, when damageApplied > 0 (+ kill_streak reset)
 *   - `onAttacked`       → target side, when (absorbedByShield + damageApplied) > 0
 *   - `onShieldBreak`    → target side, when shield 残量 0 化を検知 (本 PR は damage.ts
 *     経由で `shields.length === 0 && absorbedByShield > 0` で近似)
 *   - `onKill`           → resolveKillOwner(attacker), when result.killed
 *
 * 召喚体 kill: owner 帰属は `resolveKillOwner` を経由。owner 側で `onKillEvent`
 * (kill_streak +1) + reactive `onKill` 発火、軸 4 (onSummonDied) とは機構分離 (§2.6)。
 */
export function fireDamageReactiveEvents(attacker, target, result, state, shieldBrokeThisHit) {
    // attacker side reactive
    if (result.hit && result.damageApplied + result.absorbedByShield > 0) {
        fireReactiveSkills(state, attacker.id, 'onHit', target);
    }
    if (result.crit) {
        fireReactiveSkills(state, attacker.id, 'onCrit', target);
    }
    // target side reactive
    if (result.damageApplied > 0) {
        onTakenDamageReset(target.id, state);
        fireReactiveSkills(state, target.id, 'onTakenDamage', attacker);
    }
    if (result.absorbedByShield + result.damageApplied > 0) {
        activateDamageReactEvent(state, target.id, 'attacked');
        fireReactiveSkills(state, target.id, 'onAttacked', attacker);
    }
    if (shieldBrokeThisHit) {
        activateDamageReactEvent(state, target.id, 'shield_break');
        fireReactiveSkills(state, target.id, 'onShieldBreak', attacker);
    }
    // kill: owner 帰属 + kill_streak + reactive onKill
    if (result.killed) {
        const ownerId = resolveKillOwner(attacker, state);
        onKillEvent(ownerId, target, state);
        fireReactiveSkills(state, ownerId, 'onKill', target);
    }
}
/**
 * `applyDebuff.chanceScalesWithStatusStacks` の動的乗算係数を解決する。
 * `final_chance = base_chance × resolveDebuffChanceMultiplier(...)`。
 *
 * ADR 0014 §2.1 / §8.5:
 *   - 本 PR では数値計算 helper のみ提供 (`chanceMul` を返す)
 *   - 実際の確率ロール (`roll < base_chance × chanceMul`) は ADR 0015 (catalogue PR)
 *     で reactive-events.ts または catalogue 側で行う
 *   - 80% クランプも catalogue 側責務 (本 helper は raw multiplier のみ返す)
 *
 * 軸 2 archetype 8 (mark-debuff-link) で軸 1 mark stack 数で付与確率を動的乗算する用。
 */
export function resolveDebuffChanceMultiplier(effect, target) {
    if (effect.chanceScalesWithStatusStacks === undefined)
        return 1;
    const stacks = stackCountOf(target, effect.chanceScalesWithStatusStacks.statusId);
    return 1 + stacks * effect.chanceScalesWithStatusStacks.perStackMod;
}
/** Tick budget for status effects when a skill omits a duration. */
const DEFAULT_STATUS_TICKS = 6 * 30;
const DEFAULT_STAT_BOOST_AMOUNT = 0.05;
/** Re-entrancy depth cap for `damage.elementBranch`. ADR 0009 §2.7. */
const ELEMENT_BRANCH_DEPTH_MAX = 2;
// ===========================================================================
// Movement effects (P55-024). dash / knockback / pull / consumeNextCast.
//
// The sim models continuous position (`Entity.pos`); auto-attacks and skills
// gate on Euclidean distance (battle/tick.ts:410, isSkillInRange). These
// resolvers mutate `pos` directly. `Vec2` is readonly, so every move reassigns
// the whole object (mirrors stepTowards in battle/tick.ts). No arena bounds
// exist in the sim, so there is no clamp step (docs/05 §5.11.4 map-edge clamp
// is aspirational). knockbackResist / pullResist / immunity flags are not
// modeled and are out of scope.
// ===========================================================================
const POS_EPSILON = 1e-6;
/** Unit vector from `a` to `b`; {0,0} when coincident. Mirrors ai/types direction(). */
function unitVec(a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len <= POS_EPSILON)
        return { x: 0, y: 0 };
    return { x: dx / len, y: dy / len };
}
/** Move `entity.pos` by `dist` along unit `dir`. Reassigns pos (Vec2 readonly). */
function displace(entity, dir, dist) {
    if (dist <= 0)
        return;
    if (dir.x === 0 && dir.y === 0)
        return;
    entity.pos = { x: entity.pos.x + dir.x * dist, y: entity.pos.y + dir.y * dist };
}
/**
 * dash: the CASTER closes the gap toward the target, advancing `distance` units
 * along the caster→target ray but never overshooting past the target's contact
 * radius. Used by thief / assassin (melee gap-closers). P55-024.
 */
export function resolveDash(effect, caster, target) {
    if (caster.id === target.id)
        return; // self-target dash is a no-op
    const dir = unitVec(caster.pos, target.pos);
    if (dir.x === 0 && dir.y === 0)
        return;
    const gap = Math.hypot(target.pos.x - caster.pos.x, target.pos.y - caster.pos.y);
    const stopAt = Math.max(0, gap - (caster.radius + target.radius)); // don't overlap
    displace(caster, dir, Math.min(effect.distance, stopAt));
}
/**
 * knockback: push the TARGET away from the caster along the caster→target ray
 * (docs/05 §5.11.4: 「攻撃者から target への外向き」). P55-024.
 */
export function resolveKnockback(effect, caster, target) {
    if (caster.id === target.id)
        return;
    const dir = unitVec(caster.pos, target.pos); // caster → target (outward)
    if (dir.x === 0 && dir.y === 0)
        return; // coincident: no defined direction
    displace(target, dir, effect.distance);
}
/**
 * pull: drag the TARGET toward the caster along the target→caster ray, stopping
 * at contact radius so it doesn't overlap the caster (docs/05 §5.11.4 引き寄せ).
 * P55-024.
 */
export function resolvePull(effect, caster, target) {
    if (caster.id === target.id)
        return;
    const dir = unitVec(target.pos, caster.pos); // target → caster (inward)
    if (dir.x === 0 && dir.y === 0)
        return;
    const gap = Math.hypot(caster.pos.x - target.pos.x, caster.pos.y - target.pos.y);
    const stopAt = Math.max(0, gap - (caster.radius + target.radius));
    displace(target, dir, Math.min(effect.distance, stopAt));
}
/**
 * consumeNextCast: tag the CASTER so its NEXT active cast's output is boosted by
 * `boost`. One-shot; consumed in the cast path (battle/tick.ts). No production
 * ult uses this yet (type/tests only); implemented minimally for completeness.
 * P55-024.
 */
export function resolveConsumeNextCast(effect, caster) {
    caster.pendingCastBoost = effect.boost;
}
/**
 * Resolve the entity list a skill should affect, given the AI's primary target.
 * Returns an empty array when no valid targets are available.
 */
export function selectTargets(skill, caster, primaryTarget, state) {
    const targeting = skill.targeting;
    if (!targeting) {
        return primaryTarget ? [primaryTarget] : [];
    }
    switch (targeting.shape) {
        case 'self':
            return [caster];
        case 'singleTarget':
            return primaryTarget ? [primaryTarget] : [];
        case 'circle': {
            if (!primaryTarget)
                return [];
            const r = targeting.radius ?? 5;
            const cap = targeting.maxTargets ?? Number.POSITIVE_INFINITY;
            const candidates = state.entities.filter((e) => e.side !== caster.side &&
                e.hp > 0 &&
                Math.hypot(e.pos.x - primaryTarget.pos.x, e.pos.y - primaryTarget.pos.y) <= r);
            return candidates.slice(0, cap);
        }
        case 'line':
        case 'cone':
            // Phase 1 MVP simplification: hit only the primary target.
            return primaryTarget ? [primaryTarget] : [];
        case 'allyLowestHp': {
            const allies = state.entities.filter((e) => e.side === caster.side && e.hp > 0);
            if (allies.length === 0)
                return [];
            let lowest = allies[0];
            for (let i = 1; i < allies.length; i++) {
                const e = allies[i];
                if (e.hp / e.hpMax < lowest.hp / lowest.hpMax)
                    lowest = e;
            }
            return [lowest];
        }
        case 'allAllies':
            return state.entities.filter((e) => e.side === caster.side && e.hp > 0);
    }
}
function readItemSkillTierValue(itemSkill) {
    if (itemSkill === undefined)
        return undefined;
    const tier = itemSkill.tier ?? 0;
    return itemSkill.values[tier] ?? itemSkill.values[0];
}
function isStatBoostId(statusId) {
    return statusId.startsWith('stat-boost.');
}
function skillConsumesEffectMagnitudeValue(skill) {
    if (skill.cdrRate !== undefined)
        return false;
    if (skill.kind === 'trigger' || skill.kind === 'reactive')
        return false;
    if (skill.kind !== 'passive')
        return false;
    return skill.effects.some((effect) => (effect.kind === 'applyBuff' && isStatBoostId(effect.buffId)) ||
        effect.kind === 'shield' ||
        (skill.periodTicks !== undefined && effect.kind === 'heal'));
}
function effectMagnitudeValue(skill, itemSkill) {
    if (!skillConsumesEffectMagnitudeValue(skill))
        return undefined;
    return readItemSkillTierValue(itemSkill);
}
function statBoostTarget(statusId) {
    switch (statusId) {
        case 'stat-boost.atk-bonus':
            return 'atkBonus';
        case 'stat-boost.crit-rate':
            return 'critRate';
        case 'stat-boost.crit-dmg':
            return 'critDamage';
        case 'stat-boost.armor':
            return 'armor';
        case 'stat-boost.hp-max':
            return 'hpMax';
        case 'stat-boost.move-speed':
            return 'moveSpeed';
        case 'stat-boost.accuracy':
            return 'accuracy';
        case 'stat-boost.dodge':
            return 'evasion';
        case 'stat-boost.status-resist':
            return null;
        default:
            return null;
    }
}
function applyNumericStatBoost(target, stat, amount) {
    const addCapped = (key, cap) => {
        const before = target.stats[key];
        const after = cap === undefined ? before + amount : Math.min(cap, before + amount);
        target.stats[key] = after;
        return after - before;
    };
    const addRaw = (key, delta) => {
        const before = target.stats[key];
        target.stats[key] = before + delta;
        return delta;
    };
    switch (stat) {
        case 'atkBonus':
            return addCapped('atkBonus', 4.0);
        case 'critRate':
            return addCapped('critRate', 0.75);
        case 'critDamage':
            return addCapped('critDamage', 5.0);
        case 'armor':
            return addRaw('armor', Math.max(target.stats.armor, 1) * amount);
        case 'moveSpeed':
            return addRaw('moveSpeed', target.stats.moveSpeed * amount);
        case 'accuracy':
            return addCapped('accuracy');
        case 'evasion':
            return addCapped('evasion');
        case 'hpMax': {
            const base = Math.max(target.hpMax, target.stats.hpMax, 1);
            const delta = base * amount;
            target.hpMax += delta;
            target.stats.hpMax += delta;
            target.hp += delta;
            return delta;
        }
    }
}
function revertNumericStatBoost(target, stat, amount) {
    switch (stat) {
        case 'hpMax':
            target.hpMax = Math.max(1, target.hpMax - amount);
            target.stats.hpMax = Math.max(1, target.stats.hpMax - amount);
            target.hp = Math.min(target.hp, target.hpMax);
            return;
        default:
            target.stats[stat] -= amount;
            return;
    }
}
function applyStatBoostBuff(target, buff, amountPerStack, addedStacks) {
    if (addedStacks <= 0)
        return;
    const stat = statBoostTarget(buff.id);
    if (stat === null)
        return;
    const totalApplied = applyNumericStatBoost(target, stat, amountPerStack * addedStacks);
    if (totalApplied === 0)
        return;
    buff.statBoost =
        buff.statBoost === undefined
            ? { target: stat, amount: totalApplied }
            : { target: stat, amount: buff.statBoost.amount + totalApplied };
}
export function revertStatBoostBuff(target, buff) {
    if (buff.statBoost === undefined)
        return;
    revertNumericStatBoost(target, buff.statBoost.target, buff.statBoost.amount);
}
/**
 * Resolve all of a skill's effects against its target list. Mutates entities
 * and pushes events into `state.events`.
 *
 * `mods.effectAmountMod` (Activation jobTypeBonus 由来) は damage/heal/shield の
 * `power` のみ乗算。ADR 0015 §4.8 / §7.2 の限定範囲規約。
 */
export function applyEffects(skill, caster, primaryTarget, state, mods) {
    const targets = selectTargets(skill, caster, primaryTarget, state);
    if (targets.length === 0)
        return;
    const effectAmountMod = mods?.effectAmountMod ?? 1;
    const itemSkillValue = effectMagnitudeValue(skill, mods?.sourceItemSkill);
    const permanentBuffs = mods?.permanentBuffs ?? skill.kind === 'passive';
    for (const effect of skill.effects) {
        for (const target of targets) {
            applyEffect(effect, caster, target, state, skill.id, effectAmountMod, itemSkillValue, permanentBuffs);
        }
    }
}
/**
 * Run a single effect against a single target. Exported so the scheduler
 * (delayed effects) and the placeable trigger pipeline can re-enter the
 * resolver consistently. ADR 0009 §2.6 / §2.9.
 *
 * `effectAmountMod` (default 1) は ADR 0015 §4.8 / §7.2 の出力量乗算規約に従い、
 * damage / heal / shield の `power` のみに乗算する。delayTicks scheduling と
 * elementBranch 再帰と placeable trigger では伝播、summon / ult-mechanics /
 * fireOnCritHooks 等の別経路 applyEffect 呼び出しは default 1 のまま (不伝播)。
 */
export function applyEffect(effect, caster, target, state, skillId, effectAmountMod = 1, itemSkillValue, permanentBuffs = false) {
    // ADR 0009 §2.6: schedule and bail if delayTicks > 0.
    // The re-fired path strips delayTicks so we don't loop forever.
    const delay = effect.delayTicks;
    if (delay !== undefined && delay > 0) {
        // Clone and remove delayTicks (omit, not set to undefined — the project
        // uses exactOptionalPropertyTypes).
        const clone = { ...effect };
        delete clone.delayTicks;
        const stripped = clone;
        enqueueEffect(state, {
            fireAt: state.tick + delay,
            effect: stripped,
            casterId: caster.id,
            primaryTargetId: target.id,
            skillId,
            // True self-cast (caster.id === target.id) → 'self' fall back. All other
            // cases (ally-single, enemy attack, area) → 'skip' on target death.
            fallbackPolicy: caster.id === target.id ? 'self' : 'skip',
            // ADR 0015 §4.8: 遅延発火時も Activation 通過時の effectAmountMod を保持。
            effectAmountMod,
            ...(itemSkillValue !== undefined ? { itemSkillValue } : {}),
            ...(permanentBuffs ? { permanentBuffs } : {}),
        });
        return;
    }
    const tick = state.tick;
    const recordRecentAttacker = (attackerId, targetId) => {
        state.recentAttackers.set(targetId, attackerId);
    };
    switch (effect.kind) {
        case 'damage': {
            // ADR 0008 §2.10: resolve 'weapon-derived' before any pipeline / event
            // surface sees the value.
            const resolvedElement = resolveDamageElement(effect.element, caster);
            // ADR 0014 §2.1: damage.scalesWithStatusStacks 解決。
            // final power = base × (1 + stacks × perStackMod)。stack は target 側を参照。
            let powerMul = 1;
            if (effect.scalesWithStatusStacks !== undefined) {
                const stacks = stackCountOf(target, effect.scalesWithStatusStacks.statusId);
                powerMul = 1 + stacks * effect.scalesWithStatusStacks.perStackMod;
            }
            // ADR 0015 §4.8 / §7.2: effectAmountMod は damage.power の出力量乗算 (限定範囲)。
            const input = {
                element: resolvedElement,
                power: effect.power * powerMul * effectAmountMod,
                missChanceBonus: projectileShieldMissChanceBonus(target, state),
            };
            const result = calculateDamage(caster, target, input, state.rng);
            if (!result.hit) {
                state.events.push({
                    kind: 'damage',
                    tick,
                    sourceId: caster.id,
                    targetId: target.id,
                    element: resolvedElement,
                    amount: 0,
                    crit: false,
                });
                return;
            }
            const shieldsBefore = target.shields.length > 0;
            const apply = applyDamageWithHooks(caster, target, result.finalDamage, state);
            const shieldBrokeThisHit = shieldsBefore && target.shields.length === 0 && apply.absorbedByShield > 0;
            state.events.push({
                kind: 'damage',
                tick,
                sourceId: caster.id,
                targetId: target.id,
                element: resolvedElement,
                amount: apply.damageApplied,
                crit: result.crit,
            });
            recordRecentAttacker(caster.id, target.id);
            if (apply.killed) {
                state.events.push({ kind: 'death', tick, targetId: target.id });
            }
            // ADR 0014 §2.1 / §2.4 / §2.6: reactive event 発火 (skill damage 経路)。
            fireDamageReactiveEvents(caster, target, { ...apply, crit: result.crit, hit: result.hit }, state, shieldBrokeThisHit);
            // ADR 0009 §2.7: fire elementBranch using the resolved element.
            if (effect.elementBranch !== undefined) {
                const branch = effect.elementBranch[resolvedElement];
                if (branch !== undefined && state.elementBranchDepth < ELEMENT_BRANCH_DEPTH_MAX) {
                    state.elementBranchDepth += 1;
                    try {
                        for (const branchEffect of branch) {
                            // ADR 0015 §4.8: 派生 elementBranch effect も同 root skill の派生なので
                            // effectAmountMod を伝播 (damage/heal/shield power 乗算が連鎖)。
                            applyEffect(branchEffect, caster, target, state, skillId, effectAmountMod, itemSkillValue, permanentBuffs);
                        }
                    }
                    finally {
                        state.elementBranchDepth -= 1;
                    }
                }
            }
            // ADR 0009 §2.8: skill-damage crit fires onCritEffects on the attacker.
            if (result.crit)
                fireOnCritHooks(caster, target, state, skillId);
            return;
        }
        case 'heal': {
            // Heal amount: caster's max HP × power × 0.30. Same scale as the bleed
            // DoT in §5.12 so heals stay proportionate to the gear-driven DPS curve.
            // ADR 0015 §4.8 / §7.2: effectAmountMod は heal.power の出力量乗算 (限定範囲)。
            const power = itemSkillValue ?? effect.power;
            const amount = caster.hpMax * power * effectAmountMod * 0.3;
            const newHp = Math.min(target.hpMax, target.hp + amount);
            const healed = newHp - target.hp;
            target.hp = newHp;
            state.events.push({
                kind: 'heal',
                tick,
                sourceId: caster.id,
                targetId: target.id,
                amount: healed,
            });
            return;
        }
        case 'shield': {
            // ADR 0015 §4.8 / §7.2: effectAmountMod は shield.power の出力量乗算 (限定範囲)。
            const power = itemSkillValue ?? effect.power;
            const amount = caster.hpMax * power * effectAmountMod * 0.2;
            target.shields.push({
                sourceId: skillId,
                amount,
                expiresAt: tick + effect.durationTicks,
            });
            return;
        }
        case 'applyDebuff': {
            // ADR 0014 §2.8 / 軸 1 §A.2: mark.* (属性印 7 種) は 1 stack 独立タイマー
            // セマンティクスを持つため専用 reducer (applyMarkStack) に委譲する。
            // chanceScalesWithStatusStacks の確率ロールは ADR 0015 (catalogue PR) スコープ
            // のため、本 reducer では effect.chanceScalesWithStatusStacks は読み取らない
            // (付与は確定)。
            if (effect.debuffId.startsWith('mark.')) {
                applyMarkStack(target, effect.debuffId, tick, state, caster.id);
                return;
            }
            const stacks = effect.stacks ?? 1;
            const dur = effect.durationTicks ?? DEFAULT_STATUS_TICKS;
            const existing = target.debuffs.find((d) => d.id === effect.debuffId);
            const prevStacks = existing?.stacks ?? 0;
            if (existing) {
                existing.stacks = Math.min(STACK_CAP, existing.stacks + stacks);
                existing.expiresAt = tick + dur;
            }
            else {
                target.debuffs.push({
                    id: effect.debuffId,
                    stacks: Math.min(STACK_CAP, stacks),
                    expiresAt: tick + dur,
                });
            }
            const newStacks = (existing ?? target.debuffs.find((d) => d.id === effect.debuffId)).stacks;
            state.events.push({
                kind: 'statusApplied',
                tick,
                sourceId: caster.id,
                targetId: target.id,
                statusId: effect.debuffId,
                stacks,
            });
            // ADR 0014 §2.4: reactive onStatusApply / onSelfDebuffed 発火 (target 側)。
            fireReactiveSkills(state, target.id, 'onStatusApply', caster);
            fireReactiveSkills(state, target.id, 'onSelfDebuffed', caster);
            // ADR 0014 §2.4 / 軸 2: stack 遷移で onStatusSevere 発火 (stacking-debuff の
            // STACK_CAP 達成瞬間。severeForm への詰め替えは ADR 0015 / Phase 4)。
            if (prevStacks < STACK_CAP && newStacks === STACK_CAP) {
                fireReactiveSkills(state, target.id, 'onStatusSevere', caster);
            }
            return;
        }
        case 'applyBuff': {
            const stacks = effect.stacks ?? 1;
            const dur = effect.durationTicks ?? (permanentBuffs ? PERMANENT_BUFF_EXPIRES_AT : DEFAULT_STATUS_TICKS);
            const expiresAt = dur === PERMANENT_BUFF_EXPIRES_AT ? dur : tick + dur;
            const existing = target.buffs.find((b) => b.id === effect.buffId);
            const prevStacks = existing?.stacks ?? 0;
            let targetBuff;
            if (existing) {
                const nextStacks = Math.min(STACK_CAP, existing.stacks + stacks);
                existing.stacks = nextStacks;
                existing.expiresAt = expiresAt;
                // ADR 0009 §2.8: refresh onCritEffects when re-applied.
                if (effect.onCritEffects !== undefined)
                    existing.onCritEffects = effect.onCritEffects;
                targetBuff = existing;
            }
            else {
                const buff = {
                    id: effect.buffId,
                    stacks: Math.min(STACK_CAP, stacks),
                    expiresAt,
                };
                if (effect.onCritEffects !== undefined)
                    buff.onCritEffects = effect.onCritEffects;
                target.buffs.push(buff);
                targetBuff = buff;
            }
            const newStacks = targetBuff.stacks;
            const addedStacks = Math.max(0, newStacks - prevStacks);
            if (isStatBoostId(effect.buffId)) {
                applyStatBoostBuff(target, targetBuff, itemSkillValue ?? DEFAULT_STAT_BOOST_AMOUNT, addedStacks);
            }
            state.events.push({
                kind: 'statusApplied',
                tick,
                sourceId: caster.id,
                targetId: target.id,
                statusId: effect.buffId,
                stacks,
            });
            activateDamageReactEvent(state, target.id, 'self_buffed');
            // ADR 0014 §2.4: reactive onStatusApply 発火 (target 側、buff も対象)。
            fireReactiveSkills(state, target.id, 'onStatusApply', caster);
            // ADR 0014 §2.4 / 軸 2: stack 遷移で onStatusUpgrade 発火 (stacking-buff の
            // STACK_CAP 達成瞬間。upper form への詰め替えは ADR 0015 / Phase 4)。
            if (prevStacks < STACK_CAP && newStacks === STACK_CAP) {
                fireReactiveSkills(state, target.id, 'onStatusUpgrade', caster);
            }
            return;
        }
        case 'cleanse': {
            const removed = target.debuffs.splice(0, effect.count);
            for (const r of removed) {
                state.events.push({
                    kind: 'statusExpired',
                    tick,
                    targetId: target.id,
                    statusId: r.id,
                });
            }
            return;
        }
        case 'summon': {
            // ADR 0009 §2.10: spawn lifecycle records + entity avatars; reducer for
            // randomElement runs inside spawnSummons; threadLineAura is refreshed
            // every tick by tickSummons.
            spawnSummonsAttachingAura(effect.spec, caster, state);
            return;
        }
        case 'placePlaceable': {
            // ADR 0009 §2.9: resolve placement target, apply per-owner cap, spawn.
            spawnPlaceable(effect.spec, caster, target, state);
            return;
        }
        case 'counterStance': {
            // ADR 0010 §2.1: register an instance on the caster; damage hook fires
            // a reverse strike on subsequent incoming damage (default 1 counter).
            const inst = {
                ownerId: caster.id,
                expiresAt: tick + effect.durationTicks,
                counterPower: effect.counterPower,
                counterElement: effect.counterElement,
            };
            if (effect.counterDebuff !== undefined)
                inst.counterDebuff = effect.counterDebuff;
            state.counterStances.push(inst);
            return;
        }
        case 'damageStoreAndRelease': {
            // ADR 0010 §2.1: register a store pool; pre-apply hook reduces damage
            // and accumulates; per-tick maintenance fires the release on expiry.
            const pool = {
                ownerId: caster.id,
                expiresAt: tick + effect.storeDurationTicks,
                incomingDamageReduction: effect.incomingDamageReduction,
                releaseMultiplier: effect.releaseMultiplier,
                releaseShape: effect.releaseShape,
                releaseElement: effect.releaseElement,
                accumulated: 0,
            };
            if (effect.releaseRadius !== undefined)
                pool.releaseRadius = effect.releaseRadius;
            state.storeAndReleasePools.push(pool);
            return;
        }
        case 'sharedAbsorbCharge': {
            // ADR 0010 §2.5: register a pool snapshotting capacity at spawn time.
            const totalCapacity = caster.hpMax * effect.totalAbsorbRatio;
            state.sharedAbsorbPools.push({
                ownerId: caster.id,
                side: caster.side,
                position: { x: caster.pos.x, y: caster.pos.y },
                radius: effect.areaRadius,
                expiresAt: tick + effect.durationTicks,
                totalCapacity,
                consumed: 0,
                overflowToHealRatio: effect.overflowToHealRatio,
            });
            return;
        }
        case 'phaseCyclingAura': {
            // ADR 0010 §2.6: per-tick maintenance handles phase transition + buff.
            state.phaseCyclingAuras.push({
                ownerId: caster.id,
                expiresAt: tick + effect.durationTicks,
                phaseTicks: effect.phaseTicks,
                phases: effect.phases,
                buffByElement: effect.buffByElement,
                startTick: tick,
                lastFiredPhase: -1,
            });
            return;
        }
        case 'anchoredHealOnHit': {
            // ADR 0010 §2.8: target = the anchored enemy. The hook reads the anchor
            // ID at damage time; per-tick maintenance drops on expiry / anchor death.
            state.anchoredHealHooks.push({
                anchorEntityId: target.id,
                ownerId: caster.id,
                expiresAt: tick + effect.durationTicks,
                healPowerPerHit: effect.healPowerPerHit,
                payoutShape: effect.payoutShape,
            });
            return;
        }
        case 'damageConversion': {
            // ADR 0010 §2.1: target's incoming damage triggers a heal sized at
            // `damageDealt × fraction`. owner = caster (registers conversion on self).
            state.damageConversions.push({
                ownerId: caster.id,
                expiresAt: tick + effect.durationTicks,
                fraction: effect.fraction,
                payoutShape: effect.payoutShape,
            });
            return;
        }
        case 'reflectingProjectile': {
            resolveReflectingProjectile(effect, caster, state, skillId);
            return;
        }
        case 'transformOnHit': {
            resolveTransformOnHit(effect, caster, target, state, skillId);
            return;
        }
        // dash / knockback / pull / consumeNextCast: movement effects (P55-024).
        case 'dash':
            resolveDash(effect, caster, target);
            return;
        case 'knockback':
            resolveKnockback(effect, caster, target);
            return;
        case 'pull':
            resolvePull(effect, caster, target);
            return;
        case 'consumeNextCast':
            resolveConsumeNextCast(effect, caster);
            return;
        // ADR 0014 §2.1 / 軸 1 mark-burst / 軸 2 Debuff Consume Strike 用。
        case 'removeStatusById': {
            const lists = [target.debuffs, target.buffs];
            let removedAny = false;
            for (const list of lists) {
                const idx = list.findIndex((b) => b.id === effect.statusId);
                if (idx < 0)
                    continue;
                const buff = list[idx];
                const consumed = effect.stacks === 'all' ? buff.stacks : Math.min(buff.stacks, effect.stacks);
                if (consumed <= 0)
                    continue;
                buff.stacks -= consumed;
                // ADR 0014 §2.3.3: perStackExpiresAt がある場合は最古から消費 (FIFO)。
                if (buff.perStackExpiresAt !== undefined) {
                    buff.perStackExpiresAt = buff.perStackExpiresAt.slice(consumed);
                    if (buff.perStackExpiresAt.length > 0) {
                        // expiresAt は max(perStackExpiresAt) を維持 (legacy 互換)。
                        buff.expiresAt = Math.max(...buff.perStackExpiresAt);
                    }
                }
                if (buff.stacks <= 0)
                    list.splice(idx, 1);
                removedAny = true;
                state.events.push({
                    kind: 'statusExpired',
                    tick,
                    targetId: target.id,
                    statusId: effect.statusId,
                });
                // ADR 0014 §2.4: reactive 発火 — mark.* 消費なら onMarkBurst (caster 側、
                // 軸 1 archetype 8 Burst Refund 用)、それ以外なら onStatusConsume (target 側)。
                if (effect.statusId.startsWith('mark.')) {
                    fireReactiveSkills(state, caster.id, 'onMarkBurst', target);
                }
                else {
                    fireReactiveSkills(state, target.id, 'onStatusConsume', caster);
                }
                break; // 同 statusId は buffs / debuffs どちらか一方にしか存在しない
            }
            if (!removedAny) {
                // ADR 0014 §7.8 (a): 対象 status 不存在時は no-op (例外/状態破壊なし)。
                // 既存テストは「event log に statusExpired が増えない」で挙動を確認できる。
                return;
            }
            return;
        }
    }
}
/**
 * Fire onCritEffects retained on the attacker's BuffStacks. ADR 0009 §2.8.
 * Called from the skill-damage crit path here and from the auto-attack crit
 * path in `tick.ts`.
 *
 * Re-entrancy guard: while a hook is firing, nested damage that crits does
 * not re-enter this helper. This bounds recursion regardless of how
 * onCritEffects are configured.
 */
export function fireOnCritHooks(attacker, target, state, skillId) {
    if (state.critHookFiring)
        return;
    if (attacker.buffs.length === 0)
        return;
    state.critHookFiring = true;
    try {
        for (const buff of attacker.buffs) {
            if (buff.onCritEffects === undefined)
                continue;
            for (const eff of buff.onCritEffects) {
                applyEffect(eff, attacker, target, state, skillId);
            }
        }
    }
    finally {
        state.critHookFiring = false;
    }
}
//# sourceMappingURL=effects.js.map